import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import dimVectorService from '../services/dimVector';

// Async thunks
export const getDimVectors = createAsyncThunk(
  'dimVector/getDimVectors',
  async ({ page = 1, pageSize = 10, cubeId = null, dimId = null }, { rejectWithValue }) => {
    try {
      const response = await dimVectorService.getDimVectors(page, pageSize, cubeId, dimId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const getDimVector = createAsyncThunk(
  'dimVector/getDimVector',
  async (dimVectorId, { rejectWithValue }) => {
    try {
      const response = await dimVectorService.getDimVector(dimVectorId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const createDimVector = createAsyncThunk(
  'dimVector/createDimVector',
  async (dimVectorData, { rejectWithValue }) => {
    try {
      const response = await dimVectorService.createDimVector(dimVectorData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const updateDimVector = createAsyncThunk(
  'dimVector/updateDimVector',
  async ({ dimVectorId, dimVectorData }, { rejectWithValue }) => {
    try {
      const response = await dimVectorService.updateDimVector(dimVectorId, dimVectorData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const deleteDimVector = createAsyncThunk(
  'dimVector/deleteDimVector',
  async (dimVectorId, { rejectWithValue }) => {
    try {
      const response = await dimVectorService.deleteDimVector(dimVectorId);
      return { dimVectorId, response };
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const getMembersByDimension = createAsyncThunk(
  'dimVector/getMembersByDimension',
  async (dimId, { rejectWithValue }) => {
    try {
      const response = await dimVectorService.getMembersByDimension(dimId);
      return { dimId, members: response };
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

const initialState = {
  dimVectors: [],
  currentDimVector: null,
  members: {},
  isLoading: false,
  error: null,
  pagination: {
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0
  }
};

const dimVectorSlice = createSlice({
  name: 'dimVector',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentDimVector: (state) => {
      state.currentDimVector = null;
    },
    clearMembers: (state) => {
      state.members = {};
    }
  },
  extraReducers: (builder) => {
    builder
      // Get DIM_Vectors
      .addCase(getDimVectors.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getDimVectors.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dimVectors = action.payload.items;
        state.pagination = {
          total: action.payload.total,
          page: action.payload.page,
          pageSize: action.payload.page_size,
          totalPages: action.payload.total_pages
        };
      })
      .addCase(getDimVectors.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Get single DIM_Vector
      .addCase(getDimVector.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getDimVector.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentDimVector = action.payload;
      })
      .addCase(getDimVector.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Create DIM_Vector
      .addCase(createDimVector.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createDimVector.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dimVectors.unshift(action.payload);
      })
      .addCase(createDimVector.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Update DIM_Vector
      .addCase(updateDimVector.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateDimVector.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.dimVectors.findIndex(dv => dv.id === action.payload.id);
        if (index !== -1) {
          state.dimVectors[index] = action.payload;
        }
        if (state.currentDimVector && state.currentDimVector.id === action.payload.id) {
          state.currentDimVector = action.payload;
        }
      })
      .addCase(updateDimVector.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Delete DIM_Vector
      .addCase(deleteDimVector.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteDimVector.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dimVectors = state.dimVectors.filter(dv => dv.id !== action.payload.dimVectorId);
      })
      .addCase(deleteDimVector.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Get members by dimension
      .addCase(getMembersByDimension.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getMembersByDimension.fulfilled, (state, action) => {
        state.isLoading = false;
        state.members[action.payload.dimId] = action.payload.members;
      })
      .addCase(getMembersByDimension.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  }
});

export const { clearError, clearCurrentDimVector, clearMembers } = dimVectorSlice.actions;
export default dimVectorSlice.reducer;
